import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface AlertOptions {
  title?: string;
  message: string;
  okLabel?: string;
}

type PendingDialog =
  | { kind: 'confirm'; options: ConfirmOptions }
  | { kind: 'alert'; options: AlertOptions }
  | null;

const ConfirmContext = createContext<{
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  alert: (options: AlertOptions | string) => Promise<void>;
} | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingDialog>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions | string) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setPending({ kind: 'confirm', options: typeof options === 'string' ? { message: options } : options });
    });
  }, []);

  const alert = useCallback((options: AlertOptions | string) => {
    return new Promise<void>((resolve) => {
      resolverRef.current = () => resolve();
      setPending({ kind: 'alert', options: typeof options === 'string' ? { message: options } : options });
    });
  }, []);

  const settle = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, alert }}>
      {children}

      {pending && (
        <Modal open onClose={() => settle(false)} title={pending.options.title ?? (pending.kind === 'confirm' ? 'Confirmation' : 'Information')}>
          <p className="text-sm leading-relaxed text-manuscript-ink-soft">{pending.options.message}</p>
          <div className="mt-6 flex justify-end gap-3">
            {pending.kind === 'confirm' ? (
              <>
                <Button variant="secondary" onClick={() => settle(false)}>
                  {pending.options.cancelLabel ?? 'Annuler'}
                </Button>
                <Button
                  variant={pending.options.danger ? 'primary' : 'primary'}
                  className={pending.options.danger ? '!bg-red-500 hover:!bg-red-400' : ''}
                  onClick={() => settle(true)}
                >
                  {pending.options.confirmLabel ?? 'Confirmer'}
                </Button>
              </>
            ) : (
              <Button onClick={() => settle(true)}>{pending.options.okLabel ?? 'OK'}</Button>
            )}
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return ctx;
}
